import { ChangeDetectionStrategy, Component, inject, input, OnInit, output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ExportRangeType } from "types";
import { RouterModel } from "router-module";
import { take } from "rxjs";
import moment from "moment";
import { CardModule } from "primeng/card";
import { DatePickerModule } from "primeng/datepicker";
import { RippleModule } from "primeng/ripple";
import { exportDialogForm } from "./export-dialog-form";
import { ErrorDictionaryEntry } from "types";
import { RadioButtonModule } from "primeng/radiobutton";
import { DATE_FORMAT } from "types";
import { DatePickerInputComponent } from "../date-picker-input";
import { ModalComponent } from "../modal/modal.component";
import { RadioButtonComponent } from "../radio-button/radio-button.component";
import { I18nPluralPipe } from "@angular/common";

const getMonthsPluralMap = (): { [k: string]: string } => ({
  "=1": $localize`:@@common|date-range-months-error.one:Period must be no longer than 1 month`,
  few: $localize`:@@common|date-range-months-error.few:Period must be no longer than # months`,
  many: $localize`:@@common|date-range-months-error.many:Period must be no longer than # months`,
  other: $localize`:@@common|date-range-months-error.other:Period must be no longer than # months`,
});

@Component({
  selector: "db-export-dialog",
  templateUrl: "./export-dialog.component.html",
  imports: [
    CardModule,
    ReactiveFormsModule,
    DatePickerModule,
    RippleModule,
    DatePickerInputComponent,
    RadioButtonModule,
    ModalComponent,
    RadioButtonComponent,
  ],
  providers: [I18nPluralPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportDialogComponent implements OnInit {
  readonly exportRangeType = ExportRangeType;

  private readonly routerModel = inject(RouterModel);
  private readonly i18nPluralPipe = inject(I18nPluralPipe);
  form!: FormGroup;

  titleText = input<string>();
  exportEntityText = input<string>();
  exportAllText = input<string>("");
  includeTodayForExportAll = input<boolean>(false);
  exportPeriodMaxLimitMonths = input<number>(36);
  exportAllPeriodInMonths = input<number>();
  isSubmitting = input<boolean>(false);

  exportClick = output<{
    timeframe: { start: string; end: string } | undefined;
  }>();
  exportCancel = output<void>();

  get datesErrorDictionary(): ErrorDictionaryEntry[] {
    return [
      {
        getLabel: () => $localize`:@@common|dates-error:End date must be after start date`,
        name: "dateRange",
      },
      {
        getLabel: (error?: { [key: string]: unknown }) => {
          const months = (error?.["maxMonths"] as number) || this.exportPeriodMaxLimitMonths();
          return this.i18nPluralPipe.transform(months, getMonthsPluralMap());
        },
        name: "dateRangeLength",
      },
    ];
  }

  getExportAllLabel(): string {
    return this.exportAllText()
      ? this.exportAllText()
      : $localize`:@@export-module|export-dialog|export-all:Export all ${this.exportEntityText()}`;
  }

  getExportTimeframeLabel(): string {
    return $localize`:@@export-module|export-dialog|export-time-frame:Export ${this.exportEntityText()} from the following date range`;
  }

  ngOnInit(): void {
    this.form = exportDialogForm(this.exportPeriodMaxLimitMonths());
  }

  toggleDateRangeActivity(): void {
    const startDateControl = this.form.get("startDate");
    const endDateControl = this.form.get("endDate");
    if (this.form.get("exportType")?.value === ExportRangeType.ALL) {
      startDateControl?.disable();
      endDateControl?.disable();
    } else {
      startDateControl?.enable();
      endDateControl?.enable();
    }
  }

  cancelHandler(): void {
    this.exportCancel.emit();
    this.routerModel.selectors.path$.pipe(take(1)).subscribe((path) => {
      const newPath = path.replace("/export", "");
      this.routerModel.actions.dispatch.navigateByUrl({ url: newPath });
    });
  }

  formSubmitHandler(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    const type = this.form.get("exportType")?.value as ExportRangeType;
    if (type === ExportRangeType.TIMEFRAME) {
      const startValue = this.form.get("startDate")?.value as string;
      const endValue = this.form.get("endDate")?.value as string;
      if (!startValue || !endValue) {
        return;
      }
      let startMoment = moment(startValue);
      let endMoment = moment(endValue);
      if (startMoment.format(DATE_FORMAT) === endMoment.format(DATE_FORMAT)) {
        startMoment = startMoment.startOf("day");
        endMoment = endMoment.endOf("day");
      }
      const timeframe = {
        start: startMoment.format(DATE_FORMAT),
        end: endMoment.format(DATE_FORMAT),
      };
      this.exportClick.emit({ timeframe });
    } else if (type === ExportRangeType.ALL) {
      const periodInMonths = this.exportAllPeriodInMonths() || this.exportPeriodMaxLimitMonths();
      if (periodInMonths > 0) {
        const endDate = this.includeTodayForExportAll() ? moment() : moment().subtract(1, "days");
        endDate.endOf("day");
        const limitMonthsAgo = moment().subtract(periodInMonths, "months").startOf("day");
        const timeframe = {
          start: limitMonthsAgo.format(DATE_FORMAT),
          end: endDate.format(DATE_FORMAT),
        };
        this.exportClick.emit({ timeframe });
      } else {
        this.exportClick.emit({ timeframe: undefined });
      }
    }
  }
}
